import type { MouseEvent as ReactMouseEvent, PropsWithChildren } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { Language } from "@ui/components/LanguageSelector";
import { LanguageSelector } from "@ui/components/LanguageSelector";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useUser } from "@ui/hooks/useUser";
import { useTranslations } from "@ui/providers/useTranslations";

import { useConfig } from "@jti/app-context/useConfig";
import type { LanguageSelectorVariantConfig } from "@jti/fe-common";
import {
  confirmSoftAgeVerification,
  hasAgeConfirmedCookie,
  isRegionIdConfirmed,
  isSoftAgeVerificationConfirmed,
  requestStorageAccessFromUserGesture,
  setRegionIdCookie,
} from "@jti/fe-common";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { DialogProps } from "../../components/Dialog";
import { BACKGROUND_OPTIONS, Dialog } from "../../components/Dialog";
import { Form, useForm } from "../../components/Form";
import type { LogoProps } from "../Logo";
import { Logo } from "../Logo";

import { SavBlockingProvider } from "./SavBlockingContext";
import { useSavBlockingController } from "./useSavBlockingController";

import styles from "./SoftAgeVerification.module.scss";

export type BirthFormInputs = {
  day: string;
  month: string;
  year: string;
  minimumAge?: string;
  regionId?: string;
};

export type BirthFormDropdowns = {
  day: { value: string };
  month: { value: string };
  year: { value: string };
  minimumAge?: string;
  regionId?: string;
};

export type BirthFormFields = BirthFormInputs | BirthFormDropdowns;

export type SoftAgeVerificationProps = {
  languages?: Language[];
  isAuthorMode?: boolean;
  logo: LogoProps;
  theme?: "light" | "dark";
  isBodyScrollDisabled?: boolean;
  backgroundColor?: keyof typeof BACKGROUND_OPTIONS;
  isLogoEnabled?: boolean;
  shouldPageReload?: boolean;
  isHardGateMode?: boolean;
  isBlockingEnabled?: boolean;
  blockingMinutes?: number;
  feedbackMessage?: string;
  variant?: LanguageSelectorVariantConfig;
} & Pick<DialogProps, "portalDomNode">;

export const SoftAgeVerification = ({
  portalDomNode = document.body,
  languages,
  backgroundColor = "blurredWhite",
  theme = "light",
  logo,
  children,
  isAuthorMode,
  isBodyScrollDisabled,
  isLogoEnabled = true,
  shouldPageReload = false,
  isHardGateMode = false,
  isBlockingEnabled = false,
  blockingMinutes = 60,
  feedbackMessage = "",
  variant = {
    mobile: "FULL_LANGUAGE_NAME",
    desktop: "SHORT_LANGUAGE_NAME",
  },
}: PropsWithChildren<SoftAgeVerificationProps>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isBlocked, isUnderage, setIsUnderage, restoreActiveBlock, triggerBlock } = useSavBlockingController({
    isBlockingEnabled,
    blockingMinutes,
  });
  const shouldBlockUnderage = isBlockingEnabled && isUnderage;
  const { mobile: mobileVariant = "FULL_LANGUAGE_NAME", desktop: desktopVariant = "SHORT_LANGUAGE_NAME" } = variant;
  const { breakpoint } = useDeviceBreakpoint();
  const { isUserLoggedIn, isCheckingUser } = useUser();
  const { __ } = useTranslations();
  const { regionSelector } = useConfig();
  const isRegionFeatureActive = regionSelector.isRegionSelectorEnabled && !!regionSelector.regionOptions.length;

  useEffect(() => {
    if (isCheckingUser) {
      return;
    }

    if (restoreActiveBlock()) {
      setIsOpen(true);
      return;
    }

    const isAgeConfirmed = isSoftAgeVerificationConfirmed();
    const isRegionSelected = !isRegionFeatureActive || isRegionIdConfirmed();

    if ((!isAgeConfirmed || !isRegionSelected) && (!isUserLoggedIn || isHardGateMode)) {
      setIsOpen(true);
    }
  }, [isUserLoggedIn, isCheckingUser, isHardGateMode, isRegionFeatureActive, restoreActiveBlock]);

  const birthForm = useForm<BirthFormFields>({
    mode: "onTouched",
    defaultValues: {
      day: "",
      month: "",
      year: "",
    },
  });

  const storageAccessRequest = useRef<Promise<boolean> | null>(null);

  // DESP-144109: In WebKit-based browsers in cross-site contexts, the cookie write can be rejected
  const requestStorageAccessOnGesture = (event: ReactMouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button[type="submit"]')) {
      storageAccessRequest.current = requestStorageAccessFromUserGesture();
    }
  };

  const verifyAge = async () => {
    await (storageAccessRequest.current ?? requestStorageAccessFromUserGesture());
    storageAccessRequest.current = null;

    if (shouldBlockUnderage) {
      dispatchTrackingEvent(TrackingEvents.TrackingAgeVerificationClick, {
        age_confirmation: "no",
      });
      triggerBlock();
      return;
    }

    dispatchTrackingEvent(TrackingEvents.TrackingAgeVerificationClick, {
      age_confirmation: "yes",
    });
    confirmSoftAgeVerification();

    let hasRegionSelection = false;
    if (isRegionFeatureActive) {
      const regionId = birthForm.getValues().regionId;
      if (regionId) {
        setRegionIdCookie(regionId);
        hasRegionSelection = true;
      }
    }

    setIsOpen(false);

    if (shouldPageReload && (hasRegionSelection || hasAgeConfirmedCookie())) {
      window.location.reload();
    }
  };

  const blockingContextValue = useMemo(
    () => ({
      isBlockingEnabled,
      blockingMinutes,
      feedbackMessage,
      isBlocked,
      isUnderage,
      setIsUnderage,
    }),
    [isBlockingEnabled, blockingMinutes, feedbackMessage, isBlocked, isUnderage, setIsUnderage]
  );

  return (
    <Dialog
      dataTestId="soft-age-verification-dialog"
      theme={theme}
      closable={false}
      isOpened={isOpen}
      fullscreen={breakpoint === "mobile"}
      backgroundBlur={BACKGROUND_OPTIONS[backgroundColor].blur}
      backgroundColor={BACKGROUND_OPTIONS[backgroundColor].color}
      portalDomNode={portalDomNode}
      className={styles.ageConfirmationDialog}
      isUnwrapped={isAuthorMode}
      isBodyScrollDisabled={isBodyScrollDisabled}
      noPadding={true}
      withContentPadding={false}
      ariaLabel={__("ageverification.soft_age_verification.dialog_label")}
    >
      <div className={styles.softAgeVerificationLanguageSelector}>
        <div>
          <LanguageSelector
            popupContext="AGE_CONFIRMATION"
            variant={breakpoint === "mobile" ? mobileVariant : desktopVariant}
            languages={languages || []}
          />
        </div>
      </div>
      <div className={styles.ageConfirmationContent} onClickCapture={requestStorageAccessOnGesture}>
        {isLogoEnabled && <Logo {...logo} isDarkMode={theme === "dark"} className={styles.logo} />}
        <Form form={birthForm} onSubmit={verifyAge}>
          <SavBlockingProvider value={blockingContextValue}>
            <div className={styles.ageConfirmationChildren}>{children}</div>
          </SavBlockingProvider>
        </Form>
      </div>
    </Dialog>
  );
};
